import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Form, Input, Select, Button, Card, App, Alert, Space, Segmented, Dropdown, Modal, Upload, Progress } from 'antd';
import { DownOutlined, DeleteOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const VideoEdit = () => {
    const { id } = useParams();
    const { message, modal } = App.useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const slugParam = params.get('slug') || '';

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [meta, setMeta] = useState({ id: id || null, slug: slugParam || '' });
    const sourceType = Form.useWatch('source_type', form);
    const [item, setItem] = useState(null);
    const videoRef = useRef(null);

    const [availableLangs, setAvailableLangs] = useState(['de','en','fr','it']);
    const [selectedLangs, setSelectedLangs] = useState(['de','en','fr','it']);
    const [activeLang, setActiveLang] = useState('en');
    const [i18nForms, setI18nForms] = useState({});
    const [uploadPct, setUploadPct] = useState(0);
    const [previewOverrideUrl, setPreviewOverrideUrl] = useState('');
    const [maxMb, setMaxMb] = useState(1024);
    const [phpLimits, setPhpLimits] = useState({ upload_max_filesize: null, post_max_size: null });
    const [categoryOptions, setCategoryOptions] = useState([]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const [langsRes, vuRes, phpRes] = await Promise.all([
                    axios.get('/v1/settings/languages'),
                    axios.get('/v1/settings/video-upload').catch(() => ({ data: { data: { max_mb: 1024 } } })),
                    axios.get('/v1/settings/php-upload-limits').catch(() => ({ data: { data: {} } }))
                ]);
                const langs = langsRes.data?.data || ['de','en','fr','it'];
                setAvailableLangs(langs);
                setSelectedLangs(langs);
                const initialActive = langs.includes('en') ? 'en' : langs[0];
                setActiveLang(initialActive);
                const vu = vuRes.data?.data || { max_mb: 1024 };
                setMaxMb(vu.max_mb || 1024);
                setPhpLimits(phpRes.data?.data || {});

                const slug = slugParam;
                if (slug) {
                    const v = await axios.get(`/v1/videos/${slug}?locale=${encodeURIComponent(initialActive)}`);
                    const data = v.data?.data;
                    console.log('[VideoEdit] Loaded video', data);
                    if (data) {
                        setMeta({ id: meta.id, slug: data.slug });
                        setItem(data);
                        form.setFieldsValue({
                            title: data.title,
                            description: data.description,
                            category: data.category,
                            language: data.language,
                            visibility: data.visibility,
                            source_type: data.source_type,
                            source_url: data.source_url || ''
                        });
                        const forms = {};
                        langs.forEach((k) => { forms[k] = { title: '', description: '', thumbnail_url: '', video_url: '' }; });
                        (data.translations || []).forEach((t) => {
                            forms[t.locale] = {
                                title: t.title || '',
                                description: t.description || '',
                                thumbnail_url: t.thumbnail_url || '',
                                video_url: t.hls_manifest_url || t.source_url || ''
                            };
                        });
                        setI18nForms(forms);
                    }
                }
                // Preload category options for current activeLang
                try {
                    const catsRes = await axios.get('/v1/settings/video-categories');
                    const cats = catsRes.data?.data || [];
                    setCategoryOptions(cats.map(c => ({ value: c.key, label: c.labels?.[activeLang] || c.labels?.en || c.key })));
                } catch {}
            } catch (e) {
                message.error('Failed to load video');
            } finally {
                setLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, slugParam]);

    const submit = async (values) => {
        if (!meta.id) { message.warning('Invalid video id'); return; }
        setSaving(true);
        try {
            await axios.put(`/v1/videos/${meta.id}`, {
                title: values.title,
                description: values.description,
                category: values.category,
                language: values.language,
                visibility: values.visibility,
                source_type: values.source_type,
                source_url: values.source_type !== 'upload' ? values.source_url : null,
            });
            message.success('Updated');
        } catch (err) {
            message.error('Update failed');
        } finally {
            setSaving(false);
        }
    };

    const saveTranslations = async () => {
        if (!meta.id) { message.warning('Missing video id'); return; }
        const translations = selectedLangs.map((locale) => ({ locale, ...i18nForms[locale] }));
        try {
            await axios.post(`/v1/videos/${meta.id}/translations`, { translations });
            message.success('Translations saved');
        } catch (e) { message.error('Save translations failed'); }
    };

    const handleDelete = async () => {
        if (!meta.id) { message.warning('Missing video id'); return; }

        modal.confirm({
            title: 'Delete Video',
            content: (
                <div>
                    <p>Are you sure you want to delete this video?</p>
                    <p><strong>{item?.title || 'Untitled Video'}</strong></p>
                    <p style={{ color: '#ff4d4f', fontSize: '14px' }}>This action cannot be undone.</p>
                </div>
            ),
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await axios.delete(`/v1/videos/${meta.id}`);
                    message.success('Video deleted successfully');
                    navigate('/admin/videos'); // Redirect to admin videos list
                } catch (error) {
                    console.error('Delete error:', error);
                    message.error(`Failed to delete video: ${error.response?.data?.message || error.message}`);
                }
            },
        });
    };

    const [pairLoading, setPairLoading] = useState(false);
    const [missingLoading, setMissingLoading] = useState(false);

    const aiTranslateMissing = async (fromLocale) => {
        if (!meta.id) { message.warning('Missing video id'); return; }
        const src = i18nForms[fromLocale] || { title: '', description: '' };
        if (!src.title && !src.description) {
            Modal.warning({ title: 'Source language is empty', content: `Please enter title or description for ${fromLocale.toUpperCase()} first.` });
            return;
        }
        setMissingLoading(true);
        const updates = { ...i18nForms };
        for (const locale of selectedLangs) {
            if (locale === fromLocale) continue;
            const dest = updates[locale] || { title: '', description: '' };
            if (!dest.title || !dest.description) {
                try {
                    const res = await axios.post(`/v1/videos/${meta.id}/translate`, { source_locale: fromLocale, target_locale: locale });
                    const data = res.data?.data;
                    if (data?.locale === locale) {
                        updates[locale] = {
                            title: (dest.title && dest.title.trim().length) ? dest.title : (data.title || ''),
                            description: (dest.description && dest.description.trim().length) ? dest.description : (data.description || ''),
                        };
                    }
                } catch (e) {}
            }
        }
        setI18nForms(updates);
        message.success('Translated missing fields');
        setMissingLoading(false);
    };

    const aiTranslatePair = async (fromLocale) => {
        if (!meta.id) { message.warning('Missing video id'); return; }
        if (fromLocale === activeLang) { message.info('Select different languages'); return; }
        const dest = i18nForms[activeLang] || { title: '', description: '' };
        if (dest.title && dest.description) { message.info('Nothing to translate. All fields are filled.'); return; }
        setPairLoading(true);
        try {
            const res = await axios.post(`/v1/videos/${meta.id}/translate`, { source_locale: fromLocale, target_locale: activeLang });
            const data = res.data?.data;
            if (data?.locale === activeLang) {
                setI18nForms(prev => ({
                    ...prev,
                    [activeLang]: {
                        title: (prev[activeLang]?.title && prev[activeLang]?.title.trim().length) ? prev[activeLang]?.title : (data.title || ''),
                        description: (prev[activeLang]?.description && prev[activeLang]?.description.trim().length) ? prev[activeLang]?.description : (data.description || ''),
                    }
                }));
                message.success(`Translated ${fromLocale.toUpperCase()} → ${activeLang.toUpperCase()}`);
            }
        } catch (e) { message.error('AI translate failed'); }
        finally { setPairLoading(false); }
    };

    const handleUpload = async ({ file }) => {
        if (!meta.id) { message.warning('Missing video id'); return; }
        // Preflight: client-side size check
        const maxBytes = (maxMb || 1024) * 1024 * 1024;
        if (file.size > maxBytes) {
            message.error(`File is too large. Max allowed is ${maxMb} MB.`);
            return;
        }
        const formData = new FormData(); formData.append('file', file);
        try {
            const res = await axios.post(`/v1/videos/${meta.id}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => { if (e.total) setUploadPct(Math.round((e.loaded * 100) / e.total)); }
            });
            message.success('Upload complete');
            const data = res.data?.data;
            if (data?.url) {
                setItem(prev => ({ ...(prev || {}), source_type: 'upload', source_url: data.url }));
            }
        } catch (e) {
            const status = e?.response?.status;
            const ex = e?.response?.data?.exception || '';
            if (status === 413 || String(ex).includes('PostTooLargeException')) {
                const um = phpLimits.upload_max_filesize || 'server default';
                const pm = phpLimits.post_max_size || 'server default';
                message.error(`Upload failed: file exceeds server limit (upload_max_filesize=${um}, post_max_size=${pm}).`);
            } else {
                message.error('Upload failed');
            }
        }
    };

    const moreLangOptions = useMemo(() => {
        const remaining = availableLangs.filter(k => !selectedLangs.includes(k));
        return remaining.map(k => ({ key: k, label: k.toUpperCase(), onClick: () => setSelectedLangs([...selectedLangs, k]) }));
    }, [availableLangs, selectedLangs]);

    if (loading) return <div style={{ padding: 24 }}>Loading...</div>;

    const renderPlayer = () => {
        if (!item) return null;
        if (item.source_type === 'youtube' && item.source_url) {
            return (
                <iframe
                    width="100%"
                    height="360"
                    src={item.source_url}
                    title={item.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            );
        }
        if (item.source_type === 'vimeo' && item.source_url) {
            return (
                <iframe
                    src={item.source_url}
                    width="100%"
                    height="360"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={item.title}
                />
            );
        }
        if ((item.source_type === 'upload' && item.source_url) || item.hls_manifest_url) {
            console.log('[VideoEdit] renderPlayer', { source_type: item.source_type, url: item.source_url, hls: item.hls_manifest_url });
            const url = previewOverrideUrl || item.hls_manifest_url || item.source_url;
            console.log('[VideoEdit] renderPlayer', { url });
            return (
                <video key={url} ref={videoRef} controls preload="metadata" style={{ width: '100%', borderRadius: 8, background: '#000' }}>
                    <source src={url} />
                </video>
            );
        }
        return null;
    };

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
            {meta.slug && (
                <Alert
                    type="success"
                    showIcon
                    message={
                        <span>
                            Editing: <Link to={`/videos/${meta.slug}`} target="_blank" rel="noopener noreferrer">/videos/{meta.slug}</Link>
                        </span>
                    }
                    style={{ marginBottom: 12 }}
                />
            )}
            <div className="video-edit-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                <style>{`@media (min-width: 992px){ .video-edit-grid{ grid-template-columns: 1.1fr 0.9fr !important; } }`}</style>
                <Card title="Video Details" variant="outlined">
                    <Form form={form} layout="vertical" onFinish={submit}>
                    <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Please enter a title' }]}>
                        <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <TextArea rows={6} />
                    </Form.Item>
                    <Form.Item name="category" label="Category">
                        <Select
                            showSearch
                            placeholder="Select a category"
                            onOpenChange={async (open) => { if (open) { try { const catsRes = await axios.get('/v1/settings/video-categories'); const cats = catsRes.data?.data || []; setCategoryOptions(cats.map(c => ({ value: c.key, label: c.labels?.[activeLang] || c.labels?.en || c.key }))); } catch {} } }}
                            options={categoryOptions}
                        />
                    </Form.Item>
                    <Form.Item name="language" label="Language">
                        <Input placeholder="en" />
                    </Form.Item>
                    <Form.Item name="visibility" label="Visibility">
                        <Select options={[{ value: 'public', label: 'Public' }, { value: 'authenticated', label: 'Authenticated' }, { value: 'role-restricted', label: 'Role-restricted' }]} />
                    </Form.Item>
                    <Form.Item name="source_type" label="Source Type">
                        <Select options={[{ value: 'upload', label: 'Upload (processing)' }, { value: 'youtube', label: 'YouTube' }, { value: 'vimeo', label: 'Vimeo' }]} />
                    </Form.Item>
                    {sourceType && sourceType !== 'upload' ? (
                        <Form.Item name="source_url" label="Source URL">
                            <Input placeholder="https://..." />
                        </Form.Item>
                    ) : null}
                    {sourceType === 'upload' && (
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', marginBottom: 6 }}>Upload Video</label>
                            <Upload.Dragger multiple={false} showUploadList={false} customRequest={handleUpload} accept="video/*">
                                <p>Drag and drop video here or click to select</p>
                            </Upload.Dragger>
                            {uploadPct > 0 && <Progress percent={uploadPct} style={{ marginTop: 12 }} />}
                            <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>Max size: {maxMb} MB</div>
                        </div>
                    )}

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6 }}>Thumbnail (fallback)</label>
                        <Upload.Dragger
                            multiple={false}
                            showUploadList={false}
                            customRequest={async ({ file }) => {
                                if (!meta.id) { message.warning('Missing video id'); return; }
                                const fd = new FormData(); fd.append('file', file);
                                try {
                                    const res = await axios.post(`/v1/videos/${meta.id}/thumbnail`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    const url = res.data?.data?.url;
                                    if (url) {
                                        setItem(prev => ({ ...(prev || {}), thumbnail_url: url }));
                                    }
                                    message.success('Fallback thumbnail uploaded');
                                } catch (e) { message.error('Thumbnail upload failed'); }
                            }}
                            accept="image/*"
                            style={{ minHeight: 140, height: 140, position: 'relative' }}
                        >
                            {item?.thumbnail_url ? (
                                <img src={item.thumbnail_url} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                            ) : (
                                <p>Drop a JPG/PNG/WEBP or click to select</p>
                            )}
                        </Upload.Dragger>
                    </div>
                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit" loading={saving}>Save changes</Button>
                                <Button
                                    type="default"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    title="Delete video"
                                >
                                    Delete
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Card>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Card title="Translations" variant="outlined">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <Segmented size="middle" options={selectedLangs.map(k => ({ label: k.toUpperCase(), value: k }))} value={activeLang} onChange={(val) => setActiveLang(val)} />
                            <Dropdown menu={{ items: moreLangOptions }}>
                                <Button icon={<DownOutlined />}>More</Button>
                            </Dropdown>
                        </div>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Input placeholder={`Title (${activeLang})`} value={i18nForms[activeLang]?.title || ''} onChange={(e) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], title: e.target.value } })} />
                            <TextArea rows={4} placeholder={`Description (${activeLang})`} value={i18nForms[activeLang]?.description || ''} onChange={(e) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], description: e.target.value } })} />
                        </Space>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12, marginBottom: 40 }}>
                            <div style={{ marginBottom: 8 }}>
                                <label style={{ display: 'block', marginBottom: 6 }}>Video for {activeLang.toUpperCase()}</label>
                                <Upload.Dragger multiple={false} showUploadList={false} customRequest={async ({ file }) => {
                                    if (!meta.id) { message.warning('Missing video id'); return; }
                                    const fd = new FormData(); fd.append('file', file); fd.append('locale', activeLang);
                                    try {
                                        console.log('[VideoEdit] Upload localized video', { id: meta.id, locale: activeLang });
                                        const up = await axios.post(`/v1/videos/${meta.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                        const url = up.data?.data?.url;
                                        if (url) {
                                            // Show inline preview inside the drop area for this locale
                                            setI18nForms(prev => ({ ...prev, [activeLang]: { ...prev[activeLang], video_url: url } }));
                                            // And update the main preview (without changing fallback)
                                            setPreviewOverrideUrl(url);
                                        }
                                        // Also reload to ensure all derived fields are consistent
                                        try {
                                            const resp = await axios.get(`/v1/videos/${meta.slug}?locale=${encodeURIComponent(activeLang)}`);
                                            console.log('[VideoEdit] Reloaded after upload', resp.data?.data);
                                            // Keep fallback untouched; just update preview via override
                                            setItem(resp.data?.data || null);
                                        } catch (e) {
                                            console.warn('[VideoEdit] Reload failed', e);
                                        }
                                        message.success('Localized video uploaded');
                                    } catch (e) {
                                        console.error('[VideoEdit] Localized video upload failed', e?.response?.data || e);
                                        message.error('Localized video upload failed');
                                    }
                                }} accept="video/*" style={{ minHeight: 140, height: 140, position: 'relative' }}>
                                    {i18nForms[activeLang]?.video_url ? (
                                        <video src={i18nForms[activeLang]?.video_url} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                                    ) : (
                                        <p>Drop a video or click to select</p>
                                    )}
                                </Upload.Dragger>
                            </div>
                            <div style={{ marginBottom: 8 }}>
                                <label style={{ display: 'block', marginBottom: 6 }}>Thumbnail for {activeLang.toUpperCase()}</label>
                                <Upload.Dragger multiple={false} showUploadList={false} customRequest={async ({ file }) => {
                                    if (!meta.id) { message.warning('Missing video id'); return; }
                                    const fd = new FormData(); fd.append('file', file); fd.append('locale', activeLang);
                                    try {
                                        const res = await axios.post(`/v1/videos/${meta.id}/thumbnail`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                        const url = res.data?.data?.url;
                                        setI18nForms(prev => ({ ...prev, [activeLang]: { ...prev[activeLang], thumbnail_url: url } }));
                                        message.success('Localized thumbnail uploaded');
                                    } catch { message.error('Thumbnail upload failed'); }
                                }} accept="image/*" style={{ minHeight: 140, height: 140, position: 'relative' }}>
                                    {i18nForms[activeLang]?.thumbnail_url ? (
                                        <img
                                            src={i18nForms[activeLang]?.thumbnail_url}
                                            alt={`Thumbnail ${activeLang}`}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none', borderRadius: 8 }}
                                        />
                                    ) : (
                                        <p>Drop a JPG/PNG/WEBP or click to select</p>
                                    )}
                                </Upload.Dragger>
                            </div>
                        </div>

                        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Button type="primary" onClick={saveTranslations}>Save translations</Button>
                            <Dropdown
                                menu={{
                                    items: selectedLangs.map(l => ({ key: l, label: `AI translate missing from ${l.toUpperCase()} → others`, onClick: () => aiTranslateMissing(l) }))
                                }}
                            >
                                <Button loading={missingLoading}>AI Translate Missing</Button>
                            </Dropdown>
                        </div>
                    </Card>

                    <Card title="Preview" variant="outlined">
                        <div style={{ position: 'relative' }}>
                            {renderPlayer() || <div style={{ color: '#888' }}>No video available. Upload a file or provide a URL.</div>}
                        </div>
                        {item && item.source_type === 'upload' && item.source_url && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#888', wordBreak: 'break-all' }}>Source: {item.source_url}</div>
                        )}
                    </Card>
                </div>
            </div>

        </div>
    );
};

export default VideoEdit;


